<template>
	<section class="kova-loader">
		<div class="kova-loader__top">
			<img alt="Kova" class="kova-loader__logo" :src="logo" />

			<div class="kova-loader__copy">
				<p class="kova-loader__subtitle">{{ subtitle }}</p>
				<h1 class="kova-loader__title">{{ title }}</h1>
			</div>
		</div>

		<img alt="" class="kova-loader__image" :src="heroImage" />

		<div class="kova-loader__bar">
			<div class="kova-loader__bar-track">
				<div class="kova-loader__bar-fill" :style="{ width: progress + '%' }" />
				<span class="kova-loader__bar-value">{{ displayProgress }}%</span>
				<span
					class="kova-loader__bar-value kova-loader__bar-value_over"
					:style="{ clipPath: `inset(0 ${100 - progress}% 0 0)` }"
					aria-hidden="true"
				>
					{{ displayProgress }}%
				</span>
			</div>
		</div>
	</section>
</template>

<script>
import anime from 'animejs'
import logo from '@/assets/kova/logo-light-green.svg'
import heroImage from '@/assets/kova/landing/loading-hero.png'

// Ascending random milestones ending at 100 so the bar reads like real loading
// rather than a linear fill.
const createSteps = () => {
	const array = []
	array.push(anime.random(10, 30))
	array.push(anime.random(array[0], 50))
	array.push(anime.random(array[1], 60))
	array.push(anime.random(array[2], 70))
	array.push(anime.random(array[3], 80))
	array.push(anime.random(array[4], 90))
	array.push(100)
	return array
}

export default {
	name: 'LandingGlpLoader',

	props: {
		duration: {
			type: Number,
			required: true,
		},

		subtitle: {
			type: String,
			required: true,
		},

		title: {
			type: String,
			required: true,
		},
	},

	// progressLoaderCompleted is auto-wired by LandingView to advance into the
	// funnel — the loader fires it once the bar finishes, with no user action.
	emits: ['progressLoaderStarted', 'progressLoaderCompleted'],

	setup() {
		return { logo, heroImage }
	},

	data() {
		return {
			progress: 0,
		}
	},

	computed: {
		displayProgress() {
			return Math.round(this.progress)
		},
	},

	created() {
		const easing = `cubicBezier(${['.37,.84,.59,.09', '.4,.75,.62,.21', '.44,.66,.66,.38'][anime.random(0, 2)]})`
		const animeTarget = { progress: 0 }

		anime({
			progress: createSteps(),
			duration: this.duration,
			targets: animeTarget,
			easing,
			begin: (e) => {
				this.$emit('progressLoaderStarted', e)
			},
			update: () => {
				this.progress = animeTarget.progress
			},
			complete: () => {
				this.progress = 100
				this.$emit('progressLoaderCompleted')
			},
		})
	},
}
</script>

<style lang="scss" scoped>
.kova-loader {
	box-sizing: border-box;
	width: 100%;
	max-width: 350px;
	margin-inline: auto;
	// dvh so the bottom-pinned bar stays above mobile browser chrome (URL bar /
	// toolbar) — bare 100vh sizes to the chrome-collapsed viewport and hides it.
	min-height: 100vh;
	min-height: 100dvh;
	padding-top: var(--size-32);
	padding-bottom: var(--size-40);

	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-20);
}

.kova-loader__top {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-28);
	width: 100%;
}

.kova-loader__logo {
	height: var(--size-28);
	width: auto;
}

.kova-loader__copy {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-12);
	width: 100%;
}

.kova-loader__subtitle {
	margin: 0;

	font-size: var(--size-16);
	line-height: var(--size-22);
	font-weight: 400;

	color: var(--Fill-White);
	text-align: center;
}

.kova-loader__title {
	margin: 0;

	font-size: var(--size-18);
	line-height: var(--size-22);
	font-weight: 600;
	letter-spacing: -0.44px;

	color: var(--Fill-White);
	text-align: center;
}

.kova-loader__image {
	display: block;
	width: 100%;
	max-width: 350px;
	height: auto;
}

.kova-loader__bar {
	box-sizing: border-box;
	width: 100%;
	height: 49px;
	padding: 5px 6px;

	border-radius: var(--size-32);
	background: rgba(255, 255, 255, 0.08);
}

.kova-loader__bar-track {
	position: relative;
	width: 100%;
	height: 39px;
}

.kova-loader__bar-fill {
	position: absolute;
	top: 0;
	left: 0;
	width: 0;
	height: 39px;

	border-radius: 100px;
	background: var(--Text-Lime);
}

.kova-loader__bar-value {
	position: absolute;
	inset: 0;

	display: flex;
	align-items: center;
	justify-content: center;

	font-size: var(--size-14);
	font-weight: 700;
	line-height: normal;

	// Base layer: lime, so the % stays legible over the dark track before the fill
	// reaches it. The _over copy repaints it dark, clipped to the fill width, so the
	// text reads dark only where the lime fill sits under it.
	color: var(--Text-Lime);
}

.kova-loader__bar-value_over {
	color: var(--Text-Accent);
}
</style>
